import { z } from 'zod';
import {
  ATTENDANCE_MODEL_VALUES,
  EMPLOYMENT_STATUS_VALUES,
  SHIFT_MODEL_VALUES,
} from '@fas/types';
import {
  booleanQuerySchema,
  emailSchema,
  isoDateSchema,
  nonEmptyTrimmed,
  optionalText,
  paginationQuerySchema,
  timeOfDaySchema,
  timeToMinutes,
  uuidSchema,
} from './common';

// ── Employment types (§7 HR "Configure employment classifications") ──────────
const employmentTypeBase = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,29}$/, 'Code must be 2-30 characters: A-Z, 0-9 and underscore'),
  name: nonEmptyTrimmed(100, 'Name'),
  description: optionalText(500),
  attendanceModel: z.enum(ATTENDANCE_MODEL_VALUES),
  active: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
});
export const createEmploymentTypeSchema = employmentTypeBase;
/** The code of an employment type is immutable once created (it is referenced by profiles). */
export const updateEmploymentTypeSchema = employmentTypeBase.omit({ code: true }).partial();
export type CreateEmploymentTypeInput = z.infer<typeof createEmploymentTypeSchema>;
export type UpdateEmploymentTypeInput = z.infer<typeof updateEmploymentTypeSchema>;

// ── Faculty profile (§10, §40.2) ─────────────────────────────────────────────
export const employeeNumberSchema = z
  .string('Employee number is required')
  .trim()
  .toUpperCase()
  .min(1, 'Employee number is required')
  .max(30, 'Employee number must be at most 30 characters')
  .regex(/^[A-Z0-9][A-Z0-9._\-/]*$/, 'Employee number may contain letters, digits, . _ - /');

export const mobileNumberSchema = z
  .string()
  .trim()
  .transform((v) => (v.length === 0 ? null : v))
  .pipe(
    z
      .string()
      .regex(/^\+?[0-9][0-9 ()-]{6,19}$/, 'Must be a valid mobile number, e.g. +63 917 123 4567')
      .nullable(),
  )
  .nullable()
  .optional();

const facultyBase = z.object({
  employeeNumber: employeeNumberSchema,
  firstName: nonEmptyTrimmed(100, 'First name'),
  middleName: optionalText(100),
  lastName: nonEmptyTrimmed(100, 'Last name'),
  suffix: optionalText(20),
  institutionalEmail: emailSchema,
  mobileNumber: mobileNumberSchema,
  employmentTypeCode: z.string().trim().toUpperCase().min(1, 'Employment type is required').max(30),
  employmentStatus: z.enum(EMPLOYMENT_STATUS_VALUES).optional(),
  collegeId: uuidSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  positionTitle: optionalText(100),
  academicRank: optionalText(100),
  hireDate: isoDateSchema.nullable().optional(),
  shiftModel: z.enum(SHIFT_MODEL_VALUES).nullable().optional(),
  regularWorkStart: timeOfDaySchema.nullable().optional(),
  regularWorkEnd: timeOfDaySchema.nullable().optional(),
  breakStart: timeOfDaySchema.nullable().optional(),
  breakEnd: timeOfDaySchema.nullable().optional(),
  workDays: z
    .array(z.number().int().min(1).max(7))
    .max(7)
    .refine((days) => new Set(days).size === days.length, 'Work days must be unique')
    .optional(),
  dailyRequiredMinutes: z.number().int().min(0).max(1440).nullable().optional(),
  weeklyRequiredMinutes: z.number().int().min(0).max(10080).nullable().optional(),
  defaultCampusId: uuidSchema.nullable().optional(),
  active: z.boolean().optional(),
});

export type FacultyWorkConfig = {
  shiftModel?: 'SINGLE' | 'SPLIT' | 'FLEXIBLE' | null;
  regularWorkStart?: string | null;
  regularWorkEnd?: string | null;
  breakStart?: string | null;
  breakEnd?: string | null;
  dailyRequiredMinutes?: number | null;
};

/**
 * Validates the full-time work configuration (§21 single/split shift). Exported so the
 * API can re-run it against the merged record on PATCH.
 */
export function workConfigIssues(v: FacultyWorkConfig): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  const start = v.regularWorkStart ? timeToMinutes(v.regularWorkStart) : null;
  const end = v.regularWorkEnd ? timeToMinutes(v.regularWorkEnd) : null;
  if ((start === null) !== (end === null)) {
    issues.push({ path: start === null ? 'regularWorkStart' : 'regularWorkEnd', message: 'Work start and end must be provided together' });
  }
  if (start !== null && end !== null && end <= start) {
    issues.push({ path: 'regularWorkEnd', message: 'Work end must be later than work start' });
  }
  if (v.shiftModel === 'SINGLE' || v.shiftModel === 'SPLIT') {
    if (start === null || end === null) {
      issues.push({ path: 'regularWorkStart', message: `A ${v.shiftModel.toLowerCase()} shift requires regular work start and end times` });
    }
  }
  if (v.shiftModel === 'SPLIT') {
    const bs = v.breakStart ? timeToMinutes(v.breakStart) : null;
    const be = v.breakEnd ? timeToMinutes(v.breakEnd) : null;
    if (bs === null || be === null) {
      issues.push({ path: 'breakStart', message: 'A split shift requires break start and end times' });
    } else if (start !== null && end !== null && !(start < bs && bs < be && be < end)) {
      issues.push({ path: 'breakStart', message: 'Break must fall strictly within the working hours' });
    }
  }
  if (v.shiftModel === 'FLEXIBLE' && !v.dailyRequiredMinutes) {
    issues.push({ path: 'dailyRequiredMinutes', message: 'A flexible shift requires daily required minutes' });
  }
  return issues;
}

function refineFaculty(v: FacultyWorkConfig, ctx: z.RefinementCtx) {
  for (const issue of workConfigIssues(v)) {
    ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
  }
}

export const createFacultySchema = facultyBase
  .extend({
    /** Also create a login account (FACULTY role) and send a password-setup email. */
    createUserAccount: z.boolean().optional(),
  })
  .superRefine(refineFaculty);
export const updateFacultySchema = facultyBase.partial().superRefine(refineFaculty);
export type CreateFacultyInput = z.infer<typeof createFacultySchema>;
export type UpdateFacultyInput = z.infer<typeof updateFacultySchema>;

export const listFacultyQuerySchema = paginationQuerySchema.extend({
  employmentTypeCode: z.string().trim().toUpperCase().max(30).optional(),
  employmentStatus: z.enum(EMPLOYMENT_STATUS_VALUES).optional(),
  collegeId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  active: booleanQuerySchema.optional(),
});
export type ListFacultyQuery = z.infer<typeof listFacultyQuerySchema>;

// ── Faculty import (§10 master data, §73 import conventions) ────────────────────
/** Columns of the faculty import template, in order. */
export const FACULTY_IMPORT_COLUMNS = [
  'employee_number',
  'first_name',
  'middle_name',
  'last_name',
  'suffix',
  'institutional_email',
  'mobile_number',
  'employment_type',
  'employment_status',
  'college_code',
  'department_code',
  'position_title',
  'academic_rank',
  'hire_date',
  'shift_model',
  'regular_work_start',
  'regular_work_end',
  'break_start',
  'break_end',
  'work_days',
  'daily_required_minutes',
  'weekly_required_minutes',
  'create_login',
] as const;
export type FacultyImportColumn = (typeof FACULTY_IMPORT_COLUMNS)[number];
export const FACULTY_IMPORT_REQUIRED_COLUMNS: readonly FacultyImportColumn[] = ['employee_number', 'first_name', 'last_name', 'institutional_email', 'employment_type'];
/** At most this many faculty per import file. */
export const FACULTY_IMPORT_MAX_ROWS = 1000;

export const facultyImportQuerySchema = z.object({
  /** true: validate only and report every row; false: create all rows or nothing. */
  dryRun: booleanQuerySchema.default(true),
});
export type FacultyImportQuery = z.infer<typeof facultyImportQuerySchema>;
